import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Box, Grid, Card, CardContent, Typography, Button, TextField } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { useLogbook } from '../../contexts/LogbookContext';
import ImageUploader from './ImageUploader';
import Alert from '@mui/material/Alert';
import { getAuthHeaders, getAuthHeader, getUploadApiBaseUrl } from '../../utils/api';

const CreateServiceLog = () => {
  const { userId } = useAuth();
  const { currentLogbookId } = useLogbook();
  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [boatNameModel, setBoatNameModel] = useState('');
  const [engineType, setEngineType] = useState('');
  const [servicePersonOrCompany, setServicePersonOrCompany] = useState('');
  const [serviceType, setServiceType] = useState('');
  const [engineService, setEngineService] = useState('');
  const [sailAndRig, setSailAndRig] = useState('');
  const [electronicsAndNavigation, setElectronicsAndNavigation] = useState('');
  const [hullAndDeck, setHullAndDeck] = useState('');
  const [rudderPropellerAndDrive, setRudderPropellerAndDrive] = useState('');
  const [fuelSystem, setFuelSystem] = useState('');
  const [batteryAndElectricalSystem, setBatteryAndElectricalSystem] = useState('');
  const [sparePartsAndReplacements, setSparePartsAndReplacements] = useState('');
  const [recommendedActions, setRecommendedActions] = useState('');
  const [costAndTime, setCostAndTime] = useState('');
  const [comments, setComments] = useState('');
  const [uploadedImages, setUploadedImages] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const { currentUser } = useAuth();
  const [showLoginMessage, setShowLoginMessage] = useState(false);
  const [createError, setCreateError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (currentUser === null) {
      navigate('/info');
    }
  }, [currentUser, navigate]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!currentUser) {
      setShowLoginMessage(true);
      return;
    }

    setShowLoginMessage(false);
    setCreateError(null);

    const formData = new FormData();
    selectedFiles.forEach((file, index) => {
      formData.append(`file${index}`, file);
    });
    formData.append('accountId', userId);
    formData.append('type', 'servicelog');

    const uploadResponse = await fetch(`${getUploadApiBaseUrl()}/api/uploads`, {
      method: 'POST',
      headers: getAuthHeader() ? { Authorization: getAuthHeader() } : {},
      body: formData
    });

    if (!uploadResponse.ok) {
      const errData = await uploadResponse.json().catch(() => ({}));
      alert(errData.message || 'Kunde inte ladda upp bilder.');
      return;
    }
    const uploadedImages = await uploadResponse.json();
    console.log('Uploaded images: ', uploadedImages);

    if (!currentLogbookId) {
      alert('Välj en loggbok i headern innan du skapar en servicelogg.');
      return;
    }
    const _id = (Math.floor(Math.random() * 1e12) + 1e12).toString().substring(1);
    console.log('_id:', _id);

    const serviceLogData = {
      _id,
      logbookId: currentLogbookId,
      title,
      date,
      boatNameModel,
      engineType,
      servicePersonOrCompany,
      serviceType,
      engineService,
      sailAndRig,
      electronicsAndNavigation,
      hullAndDeck,
      rudderPropellerAndDrive,
      fuelSystem,
      batteryAndElectricalSystem,
      sparePartsAndReplacements,
      recommendedActions,
      costAndTime,
      comments,
      images: uploadedImages
    };

    try {
      const createServiceLogResponse = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/createservicelog`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(serviceLogData)
      });

      if (!createServiceLogResponse.ok) {
        const errorText = await createServiceLogResponse.text();
        console.error('Error creating service log:', createServiceLogResponse.status, errorText);
        if (createServiceLogResponse.status === 401) {
          alert('Du är inte inloggad. Logga in igen.');
          navigate('/info');
          return;
        }
        if (createServiceLogResponse.status === 403) {
          let msg = errorText;
          try {
            const errData = JSON.parse(errorText);
            if (errData.message) msg = errData.message;
          } catch (_) {}
          setCreateError(msg);
          return;
        }
        alert(`Fel vid skapande av servicelogg: ${createServiceLogResponse.status}. ${errorText}`);
        return;
      }

      const responseData = await createServiceLogResponse.json();
      navigate(`/showservicelog/${responseData._id}`);

    } catch (error) {
      console.error('Error:', error);
      alert(`Ett fel uppstod: ${error.message}`);
    }
  };

  return (
    <Box sx={{ maxWidth: '1200px', margin: '0 auto', padding: { xs: 2, md: 4 } }}>
      <Card>
        <CardContent>
        {showLoginMessage && (
          <Alert severity="warning" onClose={() => setShowLoginMessage(false)}>
            Du måste vara inloggad för att skapa en ny servicelogg.
          </Alert>
        )}
        {createError && (
          <Alert severity="error" onClose={() => setCreateError(null)} sx={{ mb: 2 }}>
            {createError}
            <Box sx={{ mt: 1 }}>
              <Button component={Link} to="/subscription" variant="outlined" size="small">
                Gå till Prenumerationsplaner
              </Button>
            </Box>
          </Alert>
        )}
        <Typography variant="h5">Ny servicelogg</Typography>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Titel"
                value={title}
                onChange={e => setTitle(e.target.value)}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Datum"
                type="date"
                value={date}
                onChange={e => setDate(e.target.value)}
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Båtens namn/modell"
                value={boatNameModel}
                onChange={e => setBoatNameModel(e.target.value)}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Motor/typ"
                value={engineType}
                onChange={e => setEngineType(e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Serviceperson eller företag"
                value={servicePersonOrCompany}
                onChange={e => setServicePersonOrCompany(e.target.value)}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Typ av service/underhåll"
                value={serviceType}
                onChange={e => setServiceType(e.target.value)}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Motorservice"
                value={engineService}
                onChange={e => setEngineService(e.target.value)}
                fullWidth
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Segel och rigg"
                value={sailAndRig}
                onChange={e => setSailAndRig(e.target.value)}
                fullWidth
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Elektronik och navigationssystem"
                value={electronicsAndNavigation}
                onChange={e => setElectronicsAndNavigation(e.target.value)}
                fullWidth
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Skrov och däck"
                value={hullAndDeck}
                onChange={e => setHullAndDeck(e.target.value)}
                fullWidth
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Roder, propeller, och drev"
                value={rudderPropellerAndDrive}
                onChange={e => setRudderPropellerAndDrive(e.target.value)}
                fullWidth
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Bränslesystem"
                value={fuelSystem}
                onChange={e => setFuelSystem(e.target.value)}
                fullWidth
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Batteri och elsystem"
                value={batteryAndElectricalSystem}
                onChange={e => setBatteryAndElectricalSystem(e.target.value)}
                fullWidth
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Reservdelar/byten"
                value={sparePartsAndReplacements}
                onChange={e => setSparePartsAndReplacements(e.target.value)}
                fullWidth
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Rekommenderade åtgärder"
                value={recommendedActions}
                onChange={e => setRecommendedActions(e.target.value)}
                fullWidth
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Kostnad och tid"
                value={costAndTime}
                onChange={e => setCostAndTime(e.target.value)}
                fullWidth
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Kommentarer/Anmärkningar"
                value={comments}
                onChange={e => setComments(e.target.value)}
                fullWidth
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <ImageUploader onUploadSuccess={(files) => setSelectedFiles(prev => [...prev, ...files])} />
                  <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-around' }}>
                    {uploadedImages.map((imageUrl, index) => (
                      <img key={index} src={imageUrl} alt="Uppladdad bild" style={{ width: '30%', height: 'auto', margin: '1%' }} />
                    ))}
                  </div>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Button type="submit" variant="contained" color="primary">
                Skapa servicelogg
              </Button>
            </Grid>
          </Grid>
        </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CreateServiceLog;